import React, { useEffect, useMemo, useState } from 'react';

import {
  ConditionalRender,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@crossnokaye/ux-common';

import {
  ActionIconVariant,
  ButtonComponentActionConfigVariant,
  DropdownOption,
  Schedule,
  Sequence,
  SettingAction,
} from '@repo/sdk/models/components';

import { TooltipButton } from '@/components/molecules/tooltip-button';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Button } from '@/ux-common/components/button/button';
import { Stack } from '@/ux-common/components/stack/stack';
import { cn } from '@/ux-common/lib/utils';

import { ActionIcon } from './action-icon';
import { ConfirmationDescription } from './confirmation-description';
import { ActionComponentProps } from './types';
import { useHandleUpdateSettings } from './use-handle-update-settings';
import { mapLegacyButtonVariantToClassName } from '../utils';

type SettingValueKind = string | number | boolean | Schedule | Sequence | undefined;

export const ActionButtonWithConfirmation = ({ state, ...props }: ActionComponentProps) => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    // TODO: UX-3355: We probably need to specify permissions in the button config
    privilege: PRIVILEGES.writeCommands,
  });

  const { handleUpdateSettings, isPending: isUpdatingSettings } = useHandleUpdateSettings();

  // Store the data for the inputs inside the dialog.
  // The key here is the ID of the dropdown component.
  const [data, setData] = useState<Record<string, SettingValueKind>>({});
  const [open, setOpen] = useState(false);

  const buttonConfig = useMemo(() => {
    return state?.actionComponentConfig?.buttonComponentActionConfig;
  }, [state?.actionComponentConfig?.buttonComponentActionConfig]);

  const requiresConfirmation = useMemo(() => {
    return buttonConfig?.confirmationDialogConfig;
  }, [buttonConfig]);

  const confirmationIconConfig: { icon: string; variant: ActionIconVariant } = useMemo(() => {
    // Determine icon configuration and apply defaults if not specified.
    return {
      icon: requiresConfirmation?.icon?.icon ?? 'CircleAlert',
      variant: requiresConfirmation?.icon?.variant ?? ActionIconVariant.Critical,
    };
  }, [requiresConfirmation?.icon]);

  useEffect(() => {
    if (open) {
      // Initialize the data when we open the dialog.
      const id = buttonConfig?.confirmationDialogConfig?.dropdownComponentConfig?.id;
      if (id) {
        setData({
          [id as string]: undefined,
        });
      }
    }
  }, [buttonConfig, state?.stateName, open]);

  // We can only submit if all the data is defined.
  const canSubmit = data && Object.values(data).every((value) => value !== undefined);

  if (!buttonConfig || !requiresConfirmation) {
    // Should never happen, we shouldn't be in this component without a confirmation dialog config.
    return null;
  }

  const handleButtonClick = async () => {
    // Collect the actions from the dropdown option that is selected.
    let enumActions: SettingAction[] = [];
    if (buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.id) {
      const id = buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.id;
      const enumValue = data[id as string];
      if (enumValue) {
        enumActions =
          buttonConfig.confirmationDialogConfig?.dropdownComponentConfig?.options.find(
            (option: DropdownOption) => option.value === enumValue
          )?.actions?.settingActions || [];
      }
    }

    // Collect the actions from the button that opens the confirmation dialog.
    const settingActions: SettingAction[] = [
      ...(buttonConfig.actions?.settingActions || []),
      ...enumActions,
    ];

    handleUpdateSettings?.(settingActions);
  };

  const label = buttonConfig.label ?? 'Button';
  const Title = (
    <div className={'flex items-center gap-4 text-foreground'}>
      <ActionIcon
        data-testid={`${label}-action-button-confirmation-icon`}
        icon={confirmationIconConfig.icon}
        size="lg"
        variant={confirmationIconConfig.variant}
      />
      {requiresConfirmation.title || `Are you sure you want to perform this action?`}
    </div>
  );

  const buttonVariant = buttonConfig.variant ?? ButtonComponentActionConfigVariant.Default;

  const handleConfirm = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    await handleButtonClick();
    setOpen(false);
  };

  const handleCancel = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    setOpen(false);
  };

  const disabledByConfig = buttonConfig.disabled ?? false;
  const disabled = disabledByConfig || isUpdatingSettings || disabledByPermissions;
  return (
    <div onClick={(e) => e.stopPropagation()} data-state-name={state?.stateName}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <TooltipButton
            {...props}
            className={cn('text-wrap w-full', mapLegacyButtonVariantToClassName(buttonVariant))}
            data-state-name={state?.stateName}
            disabled={disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
            tooltipContent={tooltipText}
          >
            {label}
          </TooltipButton>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[525px]" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{Title}</DialogTitle>
            <DialogDescription asChild>
              <Stack>
                <ConditionalRender condition={!!requiresConfirmation.description}>
                  <div className={'flex flex-col gap-4 text-foreground'}>
                    {requiresConfirmation.description}
                  </div>
                </ConditionalRender>
                <ConfirmationDescription
                  data={data ?? {}}
                  setData={setData}
                  state={state!}
                  requiresConfirmation={requiresConfirmation!}
                />
              </Stack>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex w-full gap-2 sm:flex-row">
            <Button
              className={cn(
                'flex-1 basis-1/2',
                mapLegacyButtonVariantToClassName(
                  requiresConfirmation.confirmVariant ?? ButtonComponentActionConfigVariant.Default
                )
              )}
              disabled={!canSubmit || isUpdatingSettings}
              onClick={handleConfirm}
            >
              {requiresConfirmation.confirmLabel || 'Confirm'}
            </Button>
            <Button className="flex-1 basis-1/2 btn btn-tier2" onClick={handleCancel}>
              {requiresConfirmation.cancelLabel || 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
